import {
  EnvelopeIcon,
  LinkIcon,
  MapPinIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";

import type { KnowledgeBaseField } from "hooks/useKnowledgeBaseAPI";
import { formatPhoneNumber } from "helpers/phone";

type KnowledgeBaseEntryFieldsProps = {
  fields: readonly KnowledgeBaseField[];
};

const MAP_SEARCH_URL = "https://www.google.com/maps/search/?api=1&query=";

const LINK_CLASS_NAME =
  "font-semibold text-evergreen underline underline-offset-2";

// A url field's value reaches `href` directly, and the schemes a family could be
// sent to are not the ones an author needs. Matches the allowlist the body HTML
// is sanitized against, so the same link is safe in either place.
const SAFE_URL = /^(https?:|mailto:|tel:)/i;

// The postal parts an address is stored as, in the order they are read. Mirrors
// after-internal's `location` field handler — if the two drift, a part authored
// there simply stops being shown here.
const ADDRESS_PARTS = [
  ["address_line_1"],
  ["address_line_2"],
  ["city", "state", "zip"],
  ["country"],
] as const;

/** An address's lines, dropping any part that was left empty. */
function addressLines(value: Record<string, string>): string[] {
  return ADDRESS_PARTS.map((line) =>
    line
      .map((part) => value[part]?.trim())
      .filter((part): part is string => !!part)
      .join(", "),
  ).filter((line) => line !== "");
}

function AddressValue({ value }: { value: Record<string, string> }) {
  const lines = addressLines(value);
  if (lines.length === 0) return null;

  return (
    <a
      href={`${MAP_SEARCH_URL}${encodeURIComponent(lines.join(", "))}`}
      target="_blank"
      rel="noopener noreferrer"
      className={LINK_CLASS_NAME}
    >
      {lines.map((line) => (
        <span key={line} className="block">
          {line}
        </span>
      ))}
    </a>
  );
}

function FieldValue({ field }: { field: KnowledgeBaseField }) {
  // An address is the one value that is not a scalar.
  if (typeof field.value === "object" && field.value !== null) {
    return <AddressValue value={field.value} />;
  }

  const value = String(field.value);

  switch (field.type) {
    case "phone":
      return (
        <a href={`tel:${value}`} className={LINK_CLASS_NAME}>
          {formatPhoneNumber(value)}
        </a>
      );
    case "email":
      return (
        <a href={`mailto:${value}`} className={LINK_CLASS_NAME}>
          {value}
        </a>
      );
    case "url":
      // Not a link at all when the scheme is not one we vouch for: showing the
      // text is honest, where a dead or dangerous link is not.
      if (!SAFE_URL.test(value.trim())) return <>{value}</>;
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className={LINK_CLASS_NAME}
        >
          {value}
        </a>
      );
    // Long text is the one value that may hold authored line breaks, and it is
    // plain text rather than HTML, so they are preserved rather than collapsed.
    case "long_text":
      return <span className="whitespace-pre-line">{value}</span>;
    default:
      return <span>{value}</span>;
  }
}

function FieldIcon({ type }: { type: string }) {
  const className = "h-4 w-4 shrink-0 text-plum";
  switch (type) {
    case "phone":
      return <PhoneIcon className={className} aria-hidden="true" />;
    case "email":
      return <EnvelopeIcon className={className} aria-hidden="true" />;
    case "url":
      return <LinkIcon className={className} aria-hidden="true" />;
    case "address":
      return <MapPinIcon className={className} aria-hidden="true" />;
    default:
      return null;
  }
}

/**
 * The per-situation facts attached to an answer — a pickup address, hours, a
 * phone number. Rendered as a definition list so a screen reader reads each
 * label with its value, and so the facts stay legible rather than buried in
 * prose that has to be reworded whenever one of them changes.
 *
 * The API sends only fields that have a value, in the order the entry declares
 * them, so there is nothing to filter or sort here.
 */
export default function KnowledgeBaseEntryFields({
  fields,
}: KnowledgeBaseEntryFieldsProps) {
  if (fields.length === 0) return null;

  return (
    <dl className="mt-4 space-y-2 rounded-xl bg-ivory/60 p-4">
      {fields.map((field) => (
        <div
          key={field.key}
          className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-3"
        >
          <dt className="flex items-center gap-1.5 text-sm font-semibold text-evergreen sm:w-40 sm:shrink-0">
            <FieldIcon type={field.type} />
            {field.label}
          </dt>
          <dd className="text-base text-soil">
            <FieldValue field={field} />
          </dd>
        </div>
      ))}
    </dl>
  );
}
